// client/src/pages/admin/ManageUsers.js
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import adminService from '../../services/adminService.js';
import Input from '../../components/ui/Input.js';
import Pagination from '../../components/ui/Pagination.js';
import { formatDate } from '../../utils/formatters.js';

export default function ManageUsers() {
  const [users, setUsers] = useState([]);
  const [userTypes, setUserTypes] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');

  const fetchUsers = async () => {
    const params = new URLSearchParams({ page, limit: 50 });
    if (search) params.set('search', search);
    const data = await adminService.getUsers(params.toString());
    setUsers(data.users);
    setTotalPages(data.totalPages);
  };

  const fetchUserTypes = async () => {
    try {
      const data = await adminService.getUserTypes();
      setUserTypes(data.userTypes || []);
    } catch (err) {
      console.error('Failed to load user types:', err);
    }
  };

  useEffect(() => { fetchUserTypes(); }, []);
  useEffect(() => { fetchUsers(); }, [page, search]);

  const handleToggleAdmin = async (u) => {
    await adminService.updateUser(u.userid, { usertype: u.usertype, isadmin: !u.isadmin, isactive: u.isactive });
    fetchUsers();
  };

  const handleChangeType = async (u, newType) => {
    await adminService.updateUser(u.userid, { usertype: parseInt(newType), isadmin: u.isadmin, isactive: u.isactive });
    fetchUsers();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Soft-delete this user?')) return;
    await adminService.deleteUser(id);
    fetchUsers();
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Dashboard Back Link */}
      <div className="mb-5">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-royal-900 font-semibold text-base hover:bg-gold-light transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Dashboard
        </Link>
      </div>

      <h1 className="font-display text-3xl text-gold mb-6">Users</h1>
      <Input placeholder="Search users..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="mb-4" />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-royal-700 text-royal-200 text-left">
            <th className="py-3 px-2">Email</th>
            <th className="py-3 px-2">Name</th>
            <th className="py-3 px-2">Plan</th>
            <th className="py-3 px-2">Admin</th>
            <th className="py-3 px-2">Joined</th>
            <th className="py-3 px-2">Actions</th>
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.userid} className="border-b border-royal-800 hover:bg-royal-800/30">
                <td className="py-2 px-2 text-white">{u.email}</td>
                <td className="py-2 px-2 text-royal-200">{u.firstname} {u.lastname}</td>
                <td className="py-2 px-2">
                  {userTypes.length > 0 ? (
                    <select
                      value={String(u.usertype)}
                      onChange={(e) => handleChangeType(u, e.target.value)}
                      className="bg-royal-800 text-royal-200 border border-royal-600 rounded px-2 py-1 text-xs"
                    >
                      {userTypes.map((ut) => (
                        <option key={ut.usertypeid} value={String(ut.usertypeid)}>
                          {ut.typename}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-royal-200 text-xs">{u.typename || `Type ${u.usertype}`}</span>
                  )}
                </td>
                <td className="py-2 px-2">
                  <button onClick={() => handleToggleAdmin(u)} className={`text-xs px-2 py-0.5 rounded ${u.isadmin ? 'bg-gold text-royal-900' : 'bg-royal-700 text-royal-200'}`}>
                    {u.isadmin ? 'Admin' : 'User'}
                  </button>
                </td>
                <td className="py-2 px-2 text-royal-200 text-xs">{formatDate(u.createdat)}</td>
                <td className="py-2 px-2"><button onClick={() => handleDelete(u.userid)} className="text-red-400 text-xs">Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
    </div>
  );
}
